import { useEffect, useRef } from 'react';

/**
 * Where a first-run guide button should land INSIDE the room's Data room page
 * (owner walkthrough 2026-10-26: "Open the room" did nothing once you were in the
 * room). A guide button asks for a target; the Data room page, when it is on
 * screen, scrolls it into view and briefly highlights it. When the page is not
 * mounted yet (the button also navigates there), the request waits a few seconds
 * for it. Nothing is stored.
 */
export type GuideTarget = 'upload' | 'documents' | 'ask' | 'search' | 'manage';

type Listener = (target: GuideTarget) => void;
const listeners = new Set<Listener>();
let pending: { target: GuideTarget; at: number } | null = null;

/** How long a request waits for the page to mount. */
export const GUIDE_TARGET_WAIT_MS = 5000;

export function requestGuideTarget(target: GuideTarget): void {
  if (listeners.size > 0) {
    pending = null;
    for (const listener of listeners) listener(target);
  } else {
    pending = { target, at: Date.now() };
  }
}

/** The Data room page: act on every guide request, including one made just before it mounted. */
export function useGuideTarget(onTarget: (target: GuideTarget) => void): void {
  const latest = useRef(onTarget);
  latest.current = onTarget;
  useEffect(() => {
    const listener: Listener = (target) => latest.current(target);
    listeners.add(listener);
    if (pending) {
      const { target, at } = pending;
      pending = null;
      if (Date.now() - at < GUIDE_TARGET_WAIT_MS) listener(target);
    }
    return () => {
      listeners.delete(listener);
    };
  }, []);
}
